import * as React from 'react';
import { observable, action } from 'mobx';
import { observer } from 'mobx-react';
import { Button, Panel, Form, FormControl, FormGroup, Col, ControlLabel } from 'react-bootstrap';
import * as ApiTypes from '../../models/ApiTypes';
import { bind } from '../../utils/decorators';
import ObjektMessmittelUebernehmenModal from './ObjektMessmittelUebernehmen';
import ObjektAnlegen, { ObjektAnlegenHeader } from './ObjektAnlegen';

/** Komponenten zum Erfassen eines neuen Objektes vom Typ Scheinwerfer-Einstellprüfgerät (kurz SEP) */
@observer
export default class ObjektAnlegenSEP extends ObjektAnlegen {

  @observable objekt: Partial<ApiTypes.IObjekt> = {
    objektid: '',
    hersteller: '',
    name: 'Scheinwerfer-Einstell-Prüfgerät',
    sernr: '',
    typbezeichnung: '',
    typ: 'SEP',
    status: 'aktiv',
    bemerkung: '',
    messmittelkennung: undefined
  };

  @observable objektMeta = {
    art: '',
    bauform: '',
    baumuster: ''
  };

  @bind async handleObjektAnlegenSubmit(event: React.FormEvent<FormControl>) {
    event.preventDefault();

    const objekt = Object.assign({}, this.objekt);
    objekt.meta = [];
    objekt.meta.push({ bezeichnung: 'sep_art', inhalt: this.objektMeta.art });
    objekt.meta.push({ bezeichnung: 'sep_bauform', inhalt: this.objektMeta.bauform });
    objekt.meta.push({ bezeichnung: 'sep_baumuster', inhalt: this.objektMeta.baumuster });

    if (this.props.onAddObjekt) {
      this.props.onAddObjekt(objekt);
    }
  }

  @bind @action handleEditValueChanged(event: React.FormEvent<FormControl>) {
    const value = (event.target as HTMLInputElement).value;
    const id = (event.target as HTMLInputElement).id;

    if (id in this.objekt) {
      this.objekt[id] = value;
      return;
    }

    if (id in this.objektMeta) {
      this.objektMeta[id] = value;
    }
  }

  /** Übernimmt die Daten aus einem Messmittel der ÜO, soweit diese im ÜO Bereich vorhanden sind */
  @bind @action uebernehmeMessmittelDaten(messmittel: ApiTypes.IMessmittelUO) {
    this.objekt.objektid = messmittel.pmnummer;
    this.objekt.messmittelkennung = messmittel.pmnummer;
    this.objekt.hersteller = messmittel.hersteller;
    this.objekt.sernr = messmittel.seriennummer;
    this.objekt.typbezeichnung = messmittel.name;
    this.objektMeta.baumuster = messmittel.baumusterpruefung;
    this.hideModal();
  }

  validate() {
    return (this.objektMeta.art !== '') && (this.objektMeta.bauform !== '');
  }

  render() {
    return (
      <Form horizontal={true} onSubmit={this.handleObjektAnlegenSubmit}>
        <Panel className="panel-header-buttoned"
          header={<ObjektAnlegenHeader caption="Neues Scheinwerfer-Einstell-Prüfgerät anlegen" showModal={this.showModal}/>}
          footer={(
            <div className="text-right">
              <Button onClick={() => this.uiStore.navigateBack()}>Abbrechen</Button>
              <Button bsStyle="primary" disabled={!this.validate()}  type="submit">SEP anlegen</Button>
            </div>
          )}
        >
          <FormGroup controlId="objektid">
            <Col componentClass={ControlLabel} sm={3}>Kennzeichnung</Col>
            <Col sm={9}>
              <FormControl type="text" label="Kennzeichnung" id="objektid" value={this.objekt.objektid} onChange={this.handleEditValueChanged}/>
            </Col>
          </FormGroup>

          <FormGroup controlId="hersteller">
            <Col componentClass={ControlLabel} sm={3}>Hersteller</Col>
            <Col sm={9}>
              <FormControl type="text" label="Hersteller" id="hersteller" value={this.objekt.hersteller} onChange={this.handleEditValueChanged}/>
            </Col>
          </FormGroup>

          <FormGroup controlId="name">
            <Col componentClass={ControlLabel} sm={3}>Name</Col>
            <Col sm={9}>
              <FormControl type="text" label="Name" id="name" value={this.objekt.name} onChange={this.handleEditValueChanged}/>
            </Col>
          </FormGroup>

          <FormGroup controlId="sernr">
            <Col componentClass={ControlLabel} sm={3}>Seriennr.</Col>
            <Col sm={9}>
              <FormControl type="text" label="Seriennr." id="sernr" value={this.objekt.sernr} onChange={this.handleEditValueChanged}/>
            </Col>
          </FormGroup>

          <FormGroup controlId="typbezeichnung">
            <Col componentClass={ControlLabel} sm={3}>Typbezeichnung</Col>
            <Col sm={9}>
              <FormControl type="text" label="Typbezeichnung" id="typbezeichnung" value={this.objekt.typbezeichnung} onChange={this.handleEditValueChanged}/>
            </Col>
          </FormGroup>

          <FormGroup controlId="art" validationState={this.objektMeta.art === '' ? 'error' : undefined}>
            <Col componentClass={ControlLabel} sm={3}>Art</Col>
            <Col sm={9}>
              <FormControl componentClass="select" placeholder="select" type="text" label="Art" id="art" value={this.objektMeta.art} onChange={this.handleEditValueChanged}>
                <option value="">Bitte auswählen...</option>
                <option value="Analog ohne Niveauausgleich">Analog ohne Niveauausgleich</option>
                <option value="Digital ohne Niveauausgleich">Digital ohne Niveauausgleich</option>
                <option value="Digital mit Niveauausgleich">Digital mit Niveauausgleich</option>
              </FormControl>
            </Col>
          </FormGroup>

          <FormGroup controlId="bauform" validationState={this.objektMeta.bauform === '' ? 'error' : undefined}>
            <Col componentClass={ControlLabel} sm={3}>Bauform</Col>
            <Col sm={9}>
              <FormControl componentClass="select" placeholder="select" type="text" label="Bauform" id="bauform" value={this.objektMeta.bauform} onChange={this.handleEditValueChanged}>
                <option value="">Bitte auswählen...</option>
                <option value="Frei verfahrbar">Frei verfahrbar</option>
                <option value="Auf Schienensystem">Auf Schienensystem</option>
                <option value="Sonderbauform">Sonderbauform</option>
              </FormControl>
            </Col>
          </FormGroup>

          <FormGroup controlId="baumuster">
            <Col componentClass={ControlLabel} sm={3}>Baumuster</Col>
            <Col sm={9}>
              <FormControl type="text" label="Baumuster" id="baumuster" value={this.objektMeta.baumuster} onChange={this.handleEditValueChanged}/>
            </Col>
          </FormGroup>

          <FormGroup controlId="bemerkung">
            <Col componentClass={ControlLabel} sm={3}>Bemerkung</Col>
            <Col sm={9}>
              <FormControl type="text" label="Bemerkung" id="bemerkung" value={this.objekt.bemerkung} onChange={this.handleEditValueChanged}/>
            </Col>
          </FormGroup>

          {/* Die Funktion zur Übernahme der ÜO Messmitteldaten wird modal angezeigt */}
          <ObjektMessmittelUebernehmenModal kategorie="sep" onUebernehme={this.uebernehmeMessmittelDaten} show={this.modalVisible} onHideModal={this.hideModal}/>

        </Panel>
      </Form>
    );
  }
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/components/objekte/ObjektAnlegenSEP.tsx